import React, { useState, useEffect } from "react";
import "./RetirementData.css";
import { runRetirementCalculation } from "../../../../../src/retirement-calc/dist/index.js";
import Swal from "sweetalert2";
import api from "../../../../utils/api.js"
import socket from "../../../../context/socket.js";
// ============================================================
// VALIDATION LOGIC (Reproduced from validate.ts for JS compatibility)
// ============================================================
const MAX_MONEY = 1e11; // ₹10,000 crore
const MAX_AGE = 100;
const MIN_AGE = 18;

const bounded = (v, min, max) => {
  return Number.isFinite(v) && v >= min && v <= max;
};

const validateInputs = (i) => {
  const issues = [];
  const push = (field, message) => issues.push({ field, message });

  if (!bounded(i.currentAge, MIN_AGE, MAX_AGE)) {
    push("currentAge", `Current age must be between ${MIN_AGE} and ${MAX_AGE}.`);
  }
  if (!bounded(i.retirementAge, MIN_AGE, MAX_AGE)) {
    push("retirementAge", `Retirement age must be between ${MIN_AGE} and ${MAX_AGE}.`);
  }
  if (!bounded(i.planIncomeUntilAge, MIN_AGE, MAX_AGE + 20)) {
    push("planIncomeUntilAge", `Planning horizon must be between ${MIN_AGE} and ${MAX_AGE + 20}.`);
  }
  if (i.retirementAge < i.currentAge) {
    push("cross:retirementAge", "Retirement age cannot be less than current age.");
  }
  if (i.planIncomeUntilAge <= i.retirementAge) {
    push("cross:planIncomeUntilAge", "Plan income until age must be greater than retirement age.");
  }
  if (i.incomeBeginsAtAge < i.retirementAge) {
    push("cross:incomeBeginsAtAge", "Retirement income cannot begin before retirement.");
  }
  if (i.incomeEndsAtAge < i.incomeBeginsAtAge) {
    push("cross:incomeEndsAtAge", "Retirement income cannot end before it begins.");
  }
  if (i.incomeEndsAtAge > i.planIncomeUntilAge) {
    push("cross:incomeEndsAtAge", "Retirement income cannot continue past the planning horizon.");
  }

const moneyFields = [
  "currentMonthlyExpenses",
  "desiredRetirementLifestyleToday",
  "existingRetirementInvestments",
  "currentMonthlySip",
  "lumpSumAvailableNow",
  "expectedLumpSumAtRetirement",
  "monthlyDependableIncomeAtStart",
];

const optionalMoneyFields = [
  "currentMonthlySip",
  "desiredCorpusRemainingAtPlanEnd",
  "lumpSumAvailableNow",
  "expectedLumpSumAtRetirement",
  "healthcareEmergencyReserveAtRetirement",
  "oneTimeExpenseAtRetirement",
];

for (const f of moneyFields) {
  const v = i[f];

  // Optional fields can remain blank
  if (optionalMoneyFields.includes(f) && Number.isNaN(v)) {
    continue;
  }

  if (!Number.isFinite(v) || v < 0 || v > MAX_MONEY) {
    push(String(f), "Enter a valid amount between 0 and ₹10,000 crore.");
  }
}

const rateFields = [
  ["preRetirementInflation", 0, 0.25],
  ["postRetirementInflation", 0, 0.25],
  ["accumulationReturn", -0.05, 0.18, "Enter a return assumption of 18% or lower."],
  ["withdrawalReturn", -0.05, 0.18, "Enter a return assumption of 18% or lower."],
  ["annualSipStepUp", 0, 0.50],
  ["annualIncomeEscalation", 0, 0.30],
];

const optionalRateFields = [
  "annualSipStepUp",
];

for (const [f, min, max, customMsg] of rateFields) {
  const v = i[f];

  // Optional field can remain blank
  if (optionalRateFields.includes(f) && Number.isNaN(v)) {
    continue;
  }

  if (!bounded(v, min, max)) {
    push(
      String(f),
      customMsg ??
        `Enter a rate between ${(min * 100).toFixed(0)}% and ${(max * 100).toFixed(0)}%.`
    );
  }
}

  return issues;
};

const RETURN_INPUT_MAX_PCT = 18;
const RETURN_INPUT_WARNING_ABOVE_PCT = 13;

const highReturnAssumptionWarning = (field, valueDecimal) => {
  if (!Number.isFinite(valueDecimal)) return undefined;
  const pct = valueDecimal * 100;
  if (pct <= RETURN_INPUT_WARNING_ABOVE_PCT) return undefined;
  if (pct > RETURN_INPUT_MAX_PCT) return undefined;
  if (field === "accumulationReturn") {
    return "This is a high return assumption. Actual market-linked returns may be lower or higher. Test your plan at a lower return as well.";
  }
  return "This is a high post-retirement return assumption. It may materially overstate how long the corpus could last. Test a lower-return scenario as well.";
};

const RetirementData = ({ roomId, card_id, planYourGoals, onComplete ,clientData, retirementInvestmentValue = 0}) => {
  const [step, setStep] = useState(1);
  const [highestStep, setHighestStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [showRetirementInfo, setShowRetirementInfo] = useState(false);


  // Initialize inputs from sessionStorage if available, else default
  const [inputs, setInputs] = useState(() => {
    // const saved = sessionStorage.getItem("retirementInputs");
    // if (saved) {
    //   try {
    //     return JSON.parse(saved);
    //   } catch (e) {
    //     // ignore
    //   }
    // }
    return {
      // Step 1 — Timeline
      startYear: String(new Date().getFullYear()),
      currentAge: "",
      retirementAge: "",
      planIncomeUntilAge: "",

      // Step 2 — Lifestyle
      currentMonthlyExpenses: "",
      desiredRetirementLifestyleToday: "",

      // Step 3 — Accumulation
      existingRetirementInvestments: "",
      currentMonthlySip: "",
      annualSipStepUp: "",
      lumpSumAvailableNow: "",
      expectedLumpSumAtRetirement: "",

      // Step 4 — Retirement income & reserves
      monthlyDependableIncomeAtStart: "",
      incomeBeginsAtAge: "",
      incomeEndsAtAge: "",
      annualIncomeEscalation: "",
      healthcareEmergencyReserveAtRetirement: "",
      oneTimeExpenseAtRetirement: "",
      desiredCorpusRemainingAtPlanEnd: "",

      // Step 5 — Assumptions
      preRetirementInflation: 6,
      postRetirementInflation: 5.96,
      accumulationReturn: 14,
      withdrawalReturn: 8,
    };
  });
  // DOB of client 
  const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return "";

  const dob = new Date(dateOfBirth);
  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();

  const monthDiff = today.getMonth() - dob.getMonth();

  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < dob.getDate())
  ) {
    age--;
  }

  return age;
};
  // Chnage goalData
  const saveRetirementGoalData = async (engineInputs, result) => {
  try {
    const accumulationMonths =
      result?.accumulation?.months || [];

    const retirementCorpus =
      accumulationMonths.length > 0
        ? Number(
            accumulationMonths[accumulationMonths.length - 1]
              ?.endingBalance
          ) || 0
        : 0;
  // Existung goal data for old user
const existingActionData =
  planYourGoals?.find(
    (item) => String(item._id) === String(card_id)
  )?.action_data?.[0] || {};
  console.log("eeee",engineInputs.retirementAge,engineInputs.planIncomeUntilAge,engineInputs.currentMonthlyExpenses,retirementCorpus,engineInputs.retirementCorpus,existingActionData.startYear ||
        String(new Date().getFullYear()),)

const payload = {
  _id: roomId,
  dream_id: card_id,

  action_data: [
    {
      // NEW DATA FROM RETIREMENT FORM
      retirementAge: engineInputs.retirementAge,

      lifeExpectancy: String(
        engineInputs.planIncomeUntilAge
      ),

      amount: String(
        engineInputs.desiredRetirementLifestyleToday
      ),

      // NEW CALCULATED DATA
      retirementCorpus: String(engineInputs.expectedLumpSumAtRetirement),

      // OLD DATA IF EXISTS, OTHERWISE CURRENT YEAR
      startYear: String(
      engineInputs.startYear ||
      existingActionData.startYear ||
      new Date().getFullYear()
    ),
      currentMonthlyExpenses:engineInputs.currentMonthlyExpenses,
      monthlyDependableIncomeAtStart:engineInputs.monthlyDependableIncomeAtStart,
      incomeBeginsAtAge:engineInputs.incomeBeginsAtAge,
      incomeEndsAtAge:engineInputs.incomeEndsAtAge,
      annualIncomeEscalation:engineInputs.annualIncomeEscalation,

      // OLD _id IF EXISTS
      ...(existingActionData._id
        ? { _id: existingActionData._id }
        : {}),

      status: 1,
    },
  ],

  status: 1,
};
    console.log(
      "Saving Retirement Goal Data:",
      payload
    );

    const response = await api.post(
      "advisor/save_goal_data",
      payload
    );

    console.log(
      "Retirement goal saved:",
      response.data
    );

    return response.data;
  } catch (error) {
    console.error(
      "Error saving retirement goal:",
      error
    );

    throw error;
  }
};
  // Save to sessionStorage whenever inputs change
  useEffect(() => {
    sessionStorage.setItem("retirementInputs", JSON.stringify(inputs));
  }, [inputs]);

  const updateField = (field, value) => {
  setInputs((previous) => {
    const updatedInputs = {
      ...previous,
      [field]: value,
    };

    if (roomId) {
      socket.emit("retirementDataUpdate", {
        room: roomId,
        data: {
          inputs: updatedInputs,
          step,
          highestStep,
        },
      });
    }

    return updatedInputs;
  });

  setErrors((previous) => ({
    ...previous,
    [field]: null,
  }));
  };
  // ============================================================
// RETIREMENT REAL-TIME SYNC
// Advisor = source of truth
// Client = receives step + inputs
// ============================================================
useEffect(() => {
  if (!roomId) return;

  const handleRetirementDataUpdate = (payload) => {
    console.log("🟢 RETIREMENT SYNC RECEIVED:", payload);

    if (!payload?.data) return;

    const {
      inputs: syncedInputs,
      step: syncedStep,
      highestStep: syncedHighestStep,
    } = payload.data;

    if (syncedInputs) {
      setInputs(syncedInputs);
    }

    if (Number.isFinite(syncedStep)) {
      setStep(syncedStep);
    }

    if (Number.isFinite(syncedHighestStep)) {
      setHighestStep(syncedHighestStep);
    }
  };

  socket.on(
    "retirementDataUpdate",
    handleRetirementDataUpdate
  );

  return () => {
    socket.off(
      "retirementDataUpdate",
      handleRetirementDataUpdate
    );
  };
}, [roomId]);


  const getStepFields = (currentStep) => {
    switch (currentStep) {
      case 1:
        return ["currentAge", "retirementAge", "planIncomeUntilAge"];
      case 2:
        return ["currentMonthlyExpenses", "desiredRetirementLifestyleToday"];
      case 3:
        return [
          "existingRetirementInvestments",
          "currentMonthlySip",
          "annualSipStepUp",
          "lumpSumAvailableNow",
          "expectedLumpSumAtRetirement",
        ];
      case 4:
        return [
          "monthlyDependableIncomeAtStart",
          "incomeBeginsAtAge",
          "incomeEndsAtAge",
          "annualIncomeEscalation",
          "healthcareEmergencyReserveAtRetirement",
          "oneTimeExpenseAtRetirement",
          "desiredCorpusRemainingAtPlanEnd",
        ];
      case 5:
        return [
          "preRetirementInflation",
          "postRetirementInflation",
          "accumulationReturn",
          "withdrawalReturn",
        ];
      default:
        return [];
    }
  };
  
const getRetirementTargetReturn = (currentAge, retirementAge) => {
  if (
    currentAge === "" ||
    currentAge === null ||
    currentAge === undefined ||
    retirementAge === "" ||
    retirementAge === null ||
    retirementAge === undefined
  ) {
    return "";
  }

  const current = Number(currentAge);
  const retirement = Number(retirementAge);

  if (!Number.isFinite(current) || !Number.isFinite(retirement)) {
    return "";
  }

  const yearsToRetirement = retirement - current;

  if (yearsToRetirement <= 1) return 6;
  if (yearsToRetirement <= 2) return 8;
  if (yearsToRetirement < 5) return 10;
  if (yearsToRetirement < 7) return 12;

  return 14;
};


  const getEngineInputs = () => {
    const parse = (val, isPct = false, defaultVal = 0) => {
      if (val === "" || val === null || val === undefined) return defaultVal;
      const num = Number(val);
      if (Number.isNaN(num)) return defaultVal;
      return isPct ? (num > 1 ? num / 100 : num) : num;
    };
    
    const currentAge = parse(inputs.currentAge, false, 0);
    const retirementAge = parse(inputs.retirementAge, false, 0);
    const targetReturn = getRetirementTargetReturn(
    currentAge,
    retirementAge
  );
    const planIncomeUntilAge = parse(inputs.planIncomeUntilAge, false, 0);

    const currentExp = parse(inputs.currentMonthlyExpenses, false, 0);
    const desiredLifestyle = parse(
      inputs.desiredRetirementLifestyleToday,
      false,
      currentExp
    );

    const incomeBegins = parse(inputs.incomeBeginsAtAge, false, retirementAge);
    const incomeEnds = parse(inputs.incomeEndsAtAge, false, planIncomeUntilAge);
    
    return {
      currentAge,
      retirementAge,
      planIncomeUntilAge,

      currentMonthlyExpenses: currentExp,
      desiredRetirementLifestyleToday: desiredLifestyle,

      existingRetirementInvestments: parse(inputs.existingRetirementInvestments, false, 0),
      currentMonthlySip: parse(inputs.currentMonthlySip, false, 0),
      annualSipStepUp: parse(inputs.annualSipStepUp, true, 0),
      lumpSumAvailableNow: parse(inputs.lumpSumAvailableNow, false, 0),
      expectedLumpSumAtRetirement: parse(inputs.expectedLumpSumAtRetirement, false, 0),
      startYear: String(
      inputs.startYear || new Date().getFullYear()
    ),
      monthlyDependableIncomeAtStart: parse(inputs.monthlyDependableIncomeAtStart, false, 0),
      incomeBeginsAtAge: incomeBegins || retirementAge,
      incomeEndsAtAge: incomeEnds || planIncomeUntilAge,
      annualIncomeEscalation: parse(inputs.annualIncomeEscalation, true, 0),
      healthcareEmergencyReserveAtRetirement: parse(inputs.healthcareEmergencyReserveAtRetirement, false, 0),
      oneTimeExpenseAtRetirement: parse(inputs.oneTimeExpenseAtRetirement, false, 0),
      desiredCorpusRemainingAtPlanEnd: parse(inputs.desiredCorpusRemainingAtPlanEnd, false, 0),

      preRetirementInflation: parse(inputs.preRetirementInflation, true, 0.06),
      postRetirementInflation: parse(inputs.postRetirementInflation, true, 0.0596),
      accumulationReturn: targetReturn / 100,
      withdrawalReturn: parse(inputs.withdrawalReturn, true, 0.08),
    };
  };

  const validateCurrentStep = () => {
    const engineInputs = getEngineInputs();
    const allIssues = validateInputs(engineInputs);
    const stepFields = getStepFields(step);

    const newErrors = {};
    let hasError = false;

    // Filter issues for current step fields
    allIssues.forEach((issue) => {
      let fieldName = issue.field;
      if (fieldName.startsWith("cross:")) {
        fieldName = fieldName.split(":")[1];
      }
      if (stepFields.includes(fieldName)) {
        if (!newErrors[fieldName]) {
          newErrors[fieldName] = issue.message;
          hasError = true;
        }
      }
    });

    setErrors(newErrors);
    return !hasError;
  };

const next = async () => {
  console.log("NEXT / COMPLETE CLICKED:", step);

  if (!validateCurrentStep()) {
    console.log("VALIDATION FAILED:", errors);
    return;
  }

  if (step < 5) {
    const nextStep = step + 1;

    const newHighestStep = Math.max(
      highestStep,
      nextStep
    );

    setStep(nextStep);
    setHighestStep(newHighestStep);

    // 🔵 SYNC NEXT + CURRENT DATA TO CLIENT
    if (roomId) {
      socket.emit("retirementDataUpdate", {
        room: roomId,
        data: {
          inputs,
          step: nextStep,
          highestStep: newHighestStep,
        },
      });
    }

    return;
  }

  // ============================================================
  // COMPLETE
  // ============================================================

  const engineInputs = getEngineInputs();

const calculationInputs = {
  ...engineInputs,
  retirementAge:
    Number(engineInputs.retirementAge || 0) ,
}
  const result = runRetirementCalculation(calculationInputs);
  if (roomId) {
    socket.emit("retirement_complete", {
      room: roomId,
    });
  }
  await saveRetirementGoalData(
    engineInputs,
    result
  );

  // 🔵 SYNC FINAL RETIREMENT STATE TO CLIENT
  if (roomId) {
    socket.emit("retirementDataUpdate", {
      room: roomId,
      data: {
        inputs,
        step: 5,
        highestStep: 5,
        completed: true,
        result,
      },
    });

    sessionStorage.setItem(
      `retirementResult_${roomId}`,
      JSON.stringify(result)
    );
  }

  sessionStorage.setItem(
    "retirementResult",
    JSON.stringify(result)
  );

  if (onComplete) {
    onComplete(result);
  }
};

const back = () => {
  setErrors({});

  if (step > 1) {
    const previousStep = step - 1;

    setStep(previousStep);

    // 🔵 SYNC BACK TO CLIENT
    if (roomId) {
      socket.emit("retirementDataUpdate", {
        room: roomId,
        data: {
          inputs,
          step: previousStep,
          highestStep,
        },
      });
    }
  }
};

  // Evaluate warnings on render
  let currentWarnings = {};
  if (step === 5) {
    const parse = (val, isPct = false) => {
      if (val === "" || val === null || val === undefined) return NaN;
      const num = Number(val);
      if (Number.isNaN(num)) return NaN;
      return isPct ? num / 100 : num;
    };

    const accRet = parse(inputs.accumulationReturn, true);
    const accWarn = highReturnAssumptionWarning("accumulationReturn", accRet);
    if (accWarn) currentWarnings.accumulationReturn = accWarn;

    const withRet = parse(inputs.withdrawalReturn, true);
    const withWarn = highReturnAssumptionWarning("withdrawalReturn", withRet);
    if (withWarn) currentWarnings.withdrawalReturn = withWarn;
  }

  // Compute global errors for the side panel
  const engineInputs = getEngineInputs();
  const allIssues = validateInputs(engineInputs);
  const globalErrorsToDisplay = allIssues.filter((issue) => issue.field.startsWith("cross:"));

  useEffect(() => {
  if (!clientData?.dateOfBirth) return;
  const loadRetirementData = async () => {
  try {

  const age = calculateAge(clientData.dateOfBirth);

  const card = planYourGoals.find((item) => item._id === card_id);
  const actionData = card?.action_data?.[0];

  //get per month expense INCOME - SURPLUS = Per mon expense
        // Get personal financial ratio
      const response = await api.get(
        `advisor/personal_finance_ratio_6/${clientData._id}`
      );
      const financialData = response?.data?.final_result;
      const totalIncome = Number(financialData?.total_income) || 0;
      const monthlySurplus = Number(financialData?.monthly_surplus) || 0;
      const currentMonthlyExpenses =
        totalIncome - monthlySurplus;

  setInputs((prev) => ({
    ...prev,
    currentAge: age,
     startYear: String(
    actionData?.startYear || prev.startYear || new Date().getFullYear()
    ),
    ...(actionData && {
      planIncomeUntilAge: actionData.lifeExpectancy,
      desiredRetirementLifestyleToday: actionData.amount,
      expectedLumpSumAtRetirement: actionData.retirementCorpus,
      retirementAge: actionData.retirementAge,
      currentMonthlyExpenses:currentMonthlyExpenses||actionData.currentMonthlyExpenses,
      monthlyDependableIncomeAtStart:actionData.monthlyDependableIncomeAtStart,
      incomeBeginsAtAge:actionData.incomeBeginsAtAge,
      incomeEndsAtAge:actionData.incomeEndsAtAge,
      annualIncomeEscalation:actionData.annualIncomeEscalation,
    }),
    existingRetirementInvestments: retirementInvestmentValue,
  }));
   } catch (error) {
      console.error(
        "Error loading financial/retirement data:",
        error
      );
    }
  };
   loadRetirementData();

}, [clientData?.dateOfBirth, card_id, planYourGoals,retirementInvestmentValue]);


  return (
    <div className="retirement-calculator">

      <div className="retirement-progress">

        <div className="retirement-progress-info">
          <span>Step {step} of 5</span>

          <span>
            {Math.round((step / 5) * 100)}%
          </span>
        </div>

        <div className="retirement-progress-track">
          <div
            className="retirement-progress-value"
            style={{
              width: `${(step / 5) * 100}%`,
            }}
          />
        </div>

      </div>

      <div className="retirement-layout-container">

        <div className="retirement-main-column">

          {step === 1 && (
            <StepCard
              step="1"
              title="Timeline"
              description="Let's start with when you plan to retire and how long your retirement income should last."
              onBack={back}
              onNext={next}
              isFirst
              isLast={false}
            >

              <div className="retirement-field-grid">

                <NumberField
                  label="Current age"
                  value={inputs.currentAge}
                  suffix="years"
                  error={errors.currentAge}
                  onChange={(value) =>
                    updateField("currentAge", value)
                  }
                />

                <NumberField
                  label="Retirement age"
                  value={inputs.retirementAge}
                  suffix="years"
                  error={errors.retirementAge}
                  onChange={(value) =>
                    updateField("retirementAge", value)
                  }
                />

              </div>
                  <div className="retirement-field-grid">

              <NumberField
                label="Plan retirement income until age"
                value={inputs.planIncomeUntilAge}
                suffix="years"
                error={errors.planIncomeUntilAge}
                helper="The age until which your retirement plan should provide income."
                onChange={(value) =>
                  updateField(
                    "planIncomeUntilAge",
                    value
                  )
                }
              />
              <div className="retirement-field">
 <div className="retirement-field">
  <label>Retirement planning start year</label>

  <select
    className="retirement-year-select"
    value={inputs.startYear}
    onChange={(e) => updateField("startYear", e.target.value)}
  >
    {Array.from({ length: 101 }, (_, i) => 1976 + i).map((year) => (
      <option key={year} value={year}>
        {year}
      </option>
    ))}
  </select>
</div>
</div>
                </div>

            </StepCard>
          )}


          {step === 2 && (
            <StepCard
              step="2"
              title="Lifestyle in today's rupees"
              description="Tell us about your current expenses and the lifestyle you want to maintain in retirement."
              onBack={back}
              onNext={next}
              isLast={false}
            >

              <div className="retirement-field-grid">

                <NumberField
                  label="Current monthly household expenses"
                  prefix="₹"
                  value={inputs.currentMonthlyExpenses}
                  error={errors.currentMonthlyExpenses}
                  onChange={(value) =>
                    updateField(
                      "currentMonthlyExpenses",
                      value
                    )
                  }
                />

                <NumberField
                  label="Desired retirement lifestyle (today's rupees)"
                  prefix="₹"
                  value={
                    inputs.desiredRetirementLifestyleToday
                  }
                  error={errors.desiredRetirementLifestyleToday}
                  helper="What monthly lifestyle would this cost if you retired today?"
                  onChange={(value) =>
                    updateField(
                      "desiredRetirementLifestyleToday",
                      value
                    )
                  }
                />

              </div>

            </StepCard>
          )}


          {step === 3 && (
            <StepCard
              step="3"
              title="Accumulation"
              description="Tell us what you have already accumulated."
              
              onBack={back}
              onNext={next}
              isLast={false}
            >

        {/* <NumberField
        label={
          <span>
            Existing retirement investments{" "}
            <button
              type="button"
              value={0}
              onClick={() => setShowRetirementInfo(!showRetirementInfo)}
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 0,
                marginLeft: 4,
              }}
            >
              ⓘ
            </button>
          </span>
        }
          prefix="₹"
          value={inputs.existingRetirementInvestments}
          helper={
            showRetirementInfo
              ? "Gratuity, EPF/PF, property sale proceeds, NPS, PPF, FD, mutual funds, stocks/equity, gold, SCSS, annuity/pension plans, rental income, and emergency funds."
              : ""
          }
          error={errors.existingRetirementInvestments}
          onChange={(value) =>
            updateField("existingRetirementInvestments", value)
          }
/> */}

              <div className="retirement-field-grid">

                {/* <NumberField
                  label="Current monthly SIP"
                  prefix="₹"
                  value={inputs.currentMonthlySip}
                  error={errors.currentMonthlySip}
                  onChange={(value) =>
                    updateField(
                      "currentMonthlySip",
                      value
                    )
                  }
                /> */}

                {/* <NumberField
                  label="Annual SIP step-up"
                  value={inputs.annualSipStepUp}
                  suffix="%"
                  error={errors.annualSipStepUp}
                  helper="Expected annual increase in your monthly SIP."
                  onChange={(value) =>
                    updateField(
                      "annualSipStepUp",
                      value
                    )
                  }
                /> */}

              </div>

              <div className="retirement-field-grid">

                {/* <NumberField
                  label="Lump sum available today"
                  prefix="₹"
                  value={inputs.lumpSumAvailableNow}
                  error={errors.lumpSumAvailableNow}
                  onChange={(value) =>
                    updateField(
                      "lumpSumAvailableNow",
                      value
                    )
                  }
                /> */}

                <NumberField
                  label="Expected lump sum at retirement"
                  prefix="₹"
                  helper="Gratuity, PF, property sale — anything you expect to receive at retirement."
                  value={
                    inputs.expectedLumpSumAtRetirement
                  }
                  error={errors.expectedLumpSumAtRetirement}
                  onChange={(value) =>
                    updateField(
                      "expectedLumpSumAtRetirement",
                      value
                    )
                  }
                />

              </div>

            </StepCard>
          )}


          {step === 4 && (
  <StepCard
    step="4"
    title="Retirement-stage income and reserves"
    description="Add dependable retirement income and the reserves or one-time expenses you want to account for."
    onBack={back}
    onNext={next}
    isLast={false}
  >
    <NumberField
      label="Dependable monthly income at retirement start"
      prefix="₹"
      value={inputs.monthlyDependableIncomeAtStart}
      error={errors.monthlyDependableIncomeAtStart}
      onChange={(value) =>
        updateField("monthlyDependableIncomeAtStart", value)
      }
    />

    <div className="retirement-field-grid">
      <NumberField
        label="Income begins at age"
        value={inputs.incomeBeginsAtAge}
        suffix="years"
        error={errors.incomeBeginsAtAge}
        onChange={(value) =>
          updateField("incomeBeginsAtAge", value)
        }
      />

      <NumberField
        label="Income ends at age"
        value={inputs.incomeEndsAtAge}
        suffix="years"
        error={errors.incomeEndsAtAge}
        onChange={(value) =>
          updateField("incomeEndsAtAge", value)
        }
      />
    </div>

    <NumberField
      label="Annual income escalation"
      value={inputs.annualIncomeEscalation}
      suffix="%"
      error={errors.annualIncomeEscalation}
      helper="Expected annual increase in dependable retirement income."
      onChange={(value) =>
        updateField("annualIncomeEscalation", value)
      }
    />

    {/* Hidden for now:
        Healthcare & emergency reserve
        One-time expense at retirement
    */}

    {/* <NumberField
      label="Desired corpus remaining at plan horizon"
      prefix="₹"
      value={inputs.desiredCorpusRemainingAtPlanEnd}
      error={errors.desiredCorpusRemainingAtPlanEnd}
      helper="Amount you want remaining at the end of your planning horizon."
      onChange={(value) =>
        updateField("desiredCorpusRemainingAtPlanEnd", value)
      } */}
    {/* /> */}
  </StepCard>
)}


          {step === 5 && (
            <StepCard
              step="5"
              title="Planning assumptions"
              description="These assumptions help determine how your retirement plan behaves over time."
              onBack={back}
              onNext={next}
              isLast={true}
            >

              <div className="retirement-field-grid">

                <NumberField
                  label="Pre-retirement inflation"
                  value={
                    inputs.preRetirementInflation
                  }
                  suffix="%"
                  error={errors.preRetirementInflation}
                  onChange={(value) =>
                    updateField(
                      "preRetirementInflation",
                      value
                    )
                  }
                />

                <NumberField
                  label="Post-retirement inflation"
                  value={
                    inputs.postRetirementInflation
                  }
                  suffix="%"
                  error={errors.postRetirementInflation}
                  onChange={(value) =>
                    updateField(
                      "postRetirementInflation",
                      value
                    )
                  }
                />

              </div>

              <div className="retirement-field-grid">

                <NumberField
                  label="Expected accumulation return"
                  value={getRetirementTargetReturn(
                  inputs.currentAge,
                  inputs.retirementAge
                )}
                  suffix="%"
                  error={errors.accumulationReturn}
                  warning={currentWarnings.accumulationReturn}
                  onChange={(value) =>
                    updateField(
                      "accumulationReturn",
                      value
                    )
                  }
                />

                <NumberField
                  label="Expected withdrawal-stage return"
                  value={
                    inputs.withdrawalReturn
                  }
                  suffix="%"
                  error={errors.withdrawalReturn}
                  warning={currentWarnings.withdrawalReturn}
                  onChange={(value) =>
                    updateField(
                      "withdrawalReturn",
                      value
                    )
                  }
                />

              </div>

              <div className="retirement-assumption-note">

                <div className="retirement-note-icon">
                  i
                </div>

                <div>

                  <strong>
                    Planning assumptions
                  </strong>

                  <p>
                    Returns and inflation are assumptions used
                    for planning and are not guaranteed outcomes.
                  </p>

                </div>

              </div>

            </StepCard>
          )}

        </div>

        {/* {globalErrorsToDisplay.length > 0 && (
          <div className="retirement-side-column">
            <div className="retirement-result-panel">
              <div className="retirement-error-box">
                <strong>Fix these before we can calculate:</strong>
                <ul>
                  {globalErrorsToDisplay.map((err, i) => (
                    <li key={i}>{err.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )} */}

      </div>

    </div>
  );
};


/* ============================================================
   STEP CARD
============================================================ */

const StepCard = ({
  step,
  title,
  description,
  children,
  onBack,
  onNext,
  isFirst,
  isLast,
}) => {
  return (
    <section className="retirement-card">

      <div className="retirement-step-header">

        <div className="retirement-step-number">
          {step}
        </div>

        <div>

          <div className="retirement-step-label">
            STEP {step}
          </div>

          <h2>{title}</h2>

        </div>

      </div>


      <p className="retirement-step-description">
        {description}
      </p>


      <div className="retirement-card-content">
        {children}
      </div>


      {/* ===================================================
          BOTTOM NAVIGATION
      =================================================== */}

      <div className="retirement-card-footer">

        <div>

          {!isFirst && (
            <button
              type="button"
              className="retirement-back"
              onClick={onBack}
            >
              ← Back
            </button>
          )}

        </div>


        {!isLast && (
          <button
            type="button"
            className="retirement-next"
            onClick={onNext}
          >
            Next
            <span>
              →
            </span>
          </button>
        )}
        {isLast && (
          <button
            type="button"
            className="retirement-next"
            onClick={onNext}

          >
            Complete
            <span>
              →
            </span>
          </button>
        )}
      </div>

    </section >
  );
};


/* ============================================================
   NUMBER FIELD
============================================================ */

const NumberField = ({
  label,
  value,
  prefix,
  suffix,
  helper,
  onChange,
  error,
  warning,
}) => {
  const handleInputChange = (event) => {
    let rawValue = event.target.value.replace(/[^0-9.]/g, "");
    const parts = rawValue.split(".");
    if (parts.length > 2) {
      rawValue = parts[0] + "." + parts.slice(1).join("");
    }
    onChange(rawValue);
  };

  const displayValue = () => {
    if (value === "" || value === null || value === undefined) return "";
    const strValue = String(value);
    const parts = strValue.split(".");
    let intPart = parts[0];
    const decPart = parts.length > 1 ? "." + parts[1] : "";
    if (intPart.length > 3) {
      const lastThree = intPart.slice(-3);
      const rest = intPart.slice(0, -3);
      intPart = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
    }
    return intPart + decPart;
  };

  const getCompactText = () => {
    if (prefix !== "₹" || !value) return null;
    const num = Number(value);
    if (!Number.isFinite(num) || num < 1000) return null;
    if (num >= 1e7) {
      return `₹${parseFloat((num / 1e7).toFixed(2))} Crore`;
    }
    if (num >= 1e5) {
      return `₹${parseFloat((num / 1e5).toFixed(2))} Lakh`;
    }
    if (num >= 1e3) {
      return `₹${parseFloat((num / 1e3).toFixed(2))} K`;
    }
    return null;
  };

  const compactText = getCompactText();

  return (
    <div className={`retirement-field ${error ? "has-error" : ""} ${warning && !error ? "has-warning" : ""}`}>

      <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <span>{label}</span>
        {compactText && (
          <span style={{ color: '#438c70', fontWeight: '700', fontSize: '11.5px', marginBottom: '2px' }}>
            {compactText}
          </span>
        )}
      </label>


      <div className="retirement-input-box">

        {prefix && (
          <span className="retirement-input-prefix">
            {prefix}
          </span>
        )}


        <input
          type="text"
          inputMode="numeric"
          value={displayValue()}
          onChange={handleInputChange}
        />


        {suffix && (
          <span className="retirement-input-suffix">
            {suffix}
          </span>
        )}

      </div>


      {error && (
        <div className="retirement-field-error">
          {error}
        </div>
      )}

      {!error && warning && (
        <div className="retirement-field-warning">
          {warning}
        </div>
      )}

      {!error && !warning && helper && (
        <div className="retirement-field-helper">
          {helper}
        </div>
      )}

    </div>
  );
};


export default RetirementData;